import React from "react";

interface LoadingProps {
  size?: "small" | "medium" | "large";
  fullScreen?: boolean;
  text?: string;
  inline?: boolean;
  type?: "default" | "connecting" | "reconnecting" | "error" | "processing";
  className?: string;
}

const Loading: React.FC<LoadingProps> = ({
  size = "medium",
  fullScreen = false,
  text,
  inline = false,
  type = "default",
  className = "",
}) => {
  const sizeClasses = {
    small: "w-4 h-4 border-2",
    medium: "w-8 h-8 border-4",
    large: "w-12 h-12 border-4",
  };

  const textSizeClasses = {
    small: "text-xs",
    medium: "text-sm",
    large: "text-base",
  };

  const typeColors = {
    default: "border-gray-200 border-t-primary",
    connecting: "border-gray-200 border-t-processing",
    reconnecting: "border-gray-200 border-t-error",
    error: "border-gray-200 border-t-error",
    processing: "border-gray-200 border-t-processing",
  };

  const textColors = {
    default: "text-gray-600",
    connecting: "text-processing",
    reconnecting: "text-error",
    error: "text-error",
    processing: "text-processing",
  };

  const spinner = (
    <div className={`flex flex-col items-center justify-center gap-2 ${className}`}>
      <div className={`${sizeClasses[size]} rounded-full ${typeColors[type]} animate-spin`}></div>
      {text && (
        <span className={`${textSizeClasses[size]} ${textColors[type]}`}>
          {text}
        </span>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50">
        {spinner}
      </div>
    );
  }

  if (inline) {
    return <span className="inline-flex items-center">{spinner}</span>;
  }

  return (
    <div className="flex items-center justify-center p-4">
      {spinner}
    </div>
  );
};

export default Loading;

// Typing indicator for chat
export const TypingIndicator: React.FC<{
  text?: string;
  className?: string;
}> = ({ text = "Someone is typing", className = "" }) => {
  return (
    <div className={`flex items-center gap-2 px-4 py-2 text-sm text-gray-500 ${className}`}>
      <div className="flex gap-1">
        <span className="animate-bounce">•</span>
        <span className="animate-bounce" style={{ animationDelay: "0.1s" }}>•</span>
        <span className="animate-bounce" style={{ animationDelay: "0.2s" }}>•</span>
      </div>
      <span>{text}</span>
    </div>
  );
};

// Skeleton loader component for content loading
export const SkeletonLoader: React.FC<{
  lines?: number;
  className?: string;
}> = ({ lines = 3, className = "" }) => {
  return (
    <div className={`animate-pulse ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className={`h-4 bg-gray-200 rounded mb-2 ${
            index === lines - 1 ? "w-3/4" : "w-full"
          }`}
        />
      ))}
    </div>
  );
};

// Card skeleton loader
export const CardSkeleton: React.FC<{
  className?: string;
}> = ({ className = "" }) => {
  return (
    <div className={`animate-pulse bg-white rounded-lg p-4 ${className}`}>
      <div className="h-32 bg-gray-200 rounded mb-4" />
      <div className="h-4 bg-gray-200 rounded mb-2" />
      <div className="h-4 bg-gray-200 rounded w-3/4" />
    </div>
  );
};